import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Paper,
  Stack,
  Button,
  Container,
  Typography,
  Box,
  Link,
} from '@mui/material';
import { useAuth } from '../AuthContext';
import Leaderboard from '../components/Leaderboard';
import { useLang } from '../i18n';
import HomeIt from './HomeIt';

const STEPS = [
  {
    icon: '📘',
    title: 'Курс',
    to: '/course',
    text: '20 уроков: 16 по методу Петрова и 4 урока продолжения — imperfetto, condizionale, congiuntivo. '
      + 'В каждом короткая грамматика, слова и фразы.',
  },
  {
    icon: '🇮🇹',
    title: 'Занятие',
    to: '/study',
    text: 'Главное место. Перед новым уроком — его грамматика, затем слова и спряжения. Ответ вводите сами: '
      + 'система запоминает ошибки и сама решает, когда повторить. Таймер считает только активное время.',
  },
  {
    icon: '📈',
    title: 'Прогресс',
    to: '/progress',
    text: 'Серия дней, минуты, точность по временам глаголов и прохождение уроков. Там же настройки: '
      + 'цель в минутах и сколько нового в день.',
  },
  {
    icon: '📖',
    title: 'Чтение',
    to: '/topics',
    text: 'Тексты, составленные из ваших слов и тех, где вы недавно ошибались.',
  },
];

function Home() {
  const [userName, setUserName] = useState(null);
  const { isAuthenticated } = useAuth();
  const { lang, setLang } = useLang();

  useEffect(() => {
    const name = localStorage.getItem('userName');
    if (name) {
      setUserName(name);
    }
  }, []);

  if (lang === 'it') {
    return <HomeIt userName={userName} isAuthenticated={isAuthenticated} />;
  }

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          p: { xs: 2, sm: 4 },
          boxShadow: 3,
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center' }}>
          Привет{userName ? `, ${userName}` : ''}! 👋
        </Typography>
        <Typography variant="body1" paragraph sx={{ textAlign: 'center' }}>
          Memo — приложение для изучения итальянского 🇮🇹: от первых слов до congiuntivo.
        </Typography>

        {isAuthenticated ? (
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Button component={RouterLink} to="/study" variant="contained" size="large">
              🇮🇹 Начать занятие
            </Button>
            <Leaderboard />
          </Stack>
        ) : (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3, justifyContent: 'center' }}>
              <Button component={RouterLink} to="/register" variant="contained" size="large">✍️ Зарегистрироваться</Button>
              <Button component={RouterLink} to="/login" size="large">🔐 Войти</Button>
            </Stack>
            {/* явный вход в версию для итальянцев (по языку браузера не переключаем) */}
            <Paper variant="outlined" lang="it" sx={{ p: 2, mb: 3, borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>🇮🇹 Sei italiano e vuoi imparare il russo?</Typography>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Corso di russo da zero con spiegazioni e interfaccia in italiano.
              </Typography>
              <Button variant="outlined" onClick={() => setLang('it')}>🇷🇺 Impara il russo</Button>
            </Paper>
          </>
        )}

        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: 'primary.main', borderWidth: 2, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            🎉 Версия 4.0 — новое «Занятие»
          </Typography>
          <Typography variant="body2" component="div">
            <ul style={{ paddingLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
              <li>Курс Петрова пересобран: исправлены ошибки, добавлена грамматика к каждому уроку.</li>
              <li>4 новых урока для роста до B2: imperfetto, condizionale, congiuntivo, «se facessi… farei».</li>
              <li>Спряжения в 13 временах — с таблицей при ошибке и озвучкой.</li>
              <li>Умные повторения: слово возвращается тогда, когда вы начинаете его забывать.</li>
              <li>Таймер с целью дня, серия дней и доска почёта.</li>
              <li>«Мой словарь» — свои слова в общих занятиях; «Чтение» — из ваших слов.</li>
            </ul>
            Старые карточки и словарь остались — ссылка внизу раздела «Курс». Спасибо за вашу{' '}
            <Link component={RouterLink} to="/donate">поддержку</Link>.
          </Typography>
        </Paper>

        <Typography variant="h6" gutterBottom>🤔 Как это устроено</Typography>
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          {STEPS.map((step) => (
            <Box key={step.to}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {step.icon}{' '}
                <Link component={RouterLink} to={isAuthenticated ? step.to : '/register'}>{step.title}</Link>
              </Typography>
              <Typography variant="body2">{step.text}</Typography>
            </Box>
          ))}
        </Stack>

        <Typography variant="h6" gutterBottom>📚 Советы</Typography>
        <Typography variant="body2" paragraph>
          <strong>Каждый день понемногу.</strong> 15–20 минут ежедневно дают больше, чем час раз в неделю, —
          серия дней поможет не сбиться. Удобно заниматься с телефона, например в транспорте.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Новичкам</strong> — начинать с первого урока: следующий откроется сам, когда пройдёте текущий.
          Так можно выйти на уровень{' '}
          <abbr title="Pre-Intermediate: можете рассказать о себе, семье, профессии, предпочтениях">A2</abbr>{' '}
          за пару месяцев.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Если уже знаете базу</strong> — откройте нужный урок в «Прогресс → Настройки» и добавляйте
          свои слова в «Мой словарь»: они попадут в те же занятия.
        </Typography>

        <Typography variant="h6" gutterBottom>🎥 Видео</Typography>
        <Typography variant="body2" paragraph>
          <Link href="https://youtube.com/shorts/C9OsCb6YMhs?si=xJ3xDtcgyfXYVcVv" target="_blank" rel="noopener">
            Как добавить иконку на экран телефона
          </Link>
          <br />
          <Link href="https://youtu.be/VSgh0XTe1Ig?si=JqMYCZkN5eL6t9iX" target="_blank" rel="noopener">
            Как пользоваться приложением
          </Link>{' '}
          <Typography component="span" variant="caption" color="text.secondary">(про старую версию)</Typography>
        </Typography>

        <Typography variant="h6" gutterBottom>📜 Предыстория</Typography>
        <Typography variant="body2" paragraph>
          На нашем ютюб канале{' '}
          <Link href="https://www.youtube.com/channel/UCZZAtmyerOqOgbHlxrpEUvw" target="_blank" rel="noopener">
            «Шестаковы в Италии»
          </Link>{' '}
          мы не раз рассказывали, что начинали изучать итальянский по урокам Дмитрия Петрова «Полиглот. Итальянский
          за 16 часов». На мой взгляд, это лучший метод для вхождения в язык: с тех пор я сдал несколько
          государственных итальянских экзаменов.
        </Typography>
        <Typography variant="body2" paragraph>
          Это отличный старт, но на практике нужно больше времени. Это приложение — мой инструмент для углублённого
          изучения, и я сам занимаюсь в нём каждый день. 🎓
        </Typography>

        <Typography variant="h6" gutterBottom>🚀 Итальянский вокруг вас</Typography>
        <Typography variant="body2" paragraph>
          Найдите контент по интересам на итальянском: кулинария, техника, спорт. Контекст помогает учить язык
          быстрее!
        </Typography>
        <Typography variant="body2" paragraph>
          🍽️ <Link href="https://www.youtube.com/@GialloZafferanoTV" target="_blank" rel="noopener">GialloZafferanoTV (кулинария)</Link> <br />
          🎨 <Link href="https://www.youtube.com/@fraffrog" target="_blank" rel="noopener">fraffrog (творчество)</Link> <br />
          💻 <Link href="https://www.youtube.com/@overVolt" target="_blank" rel="noopener">overVolt (технологии)</Link> <br />
          ✈️ <Link href="https://www.youtube.com/@marco_romboli" target="_blank" rel="noopener">marco_romboli (авиация)</Link> <br />
          🚴‍♀️ <Link href="https://www.youtube.com/@gcnitalia" target="_blank" rel="noopener">gcnitalia (велоспорт)</Link>
        </Typography>

        <Typography variant="h6" gutterBottom>📝 Условия использования</Typography>
        <Typography variant="body2" paragraph>
          По всем вопросам пишите на <Link href="mailto:boris@shbb.pro">boris@shbb.pro</Link>.
          Сторонняя аналитика и трекеры не используются. Ознакомиться с{' '}
          <Link component={RouterLink} to="/terms-of-service">условиями использования</Link>.
        </Typography>
      </Box>
    </Container>
  );
}

export default Home;
